import { BACKEND_URL } from '@/lib/config'
import { getAuthToken } from '@/lib/supabase'

interface DialogResponse {
  success?: boolean
  email?: string
  error?: string
}

// Check if we're in an Office environment
function isOfficeEnvironment(): boolean {
  return (
    typeof Office !== 'undefined' &&
    Office.context?.ui?.displayDialogAsync !== undefined
  )
}

/**
 * Fetch the Google OAuth URL from the backend.
 * This is an authenticated call that returns the URL to open in a popup.
 */
async function getGoogleOAuthUrl(
  permission: 'read' | 'read_send' = 'read'
): Promise<string> {
  const token = await getAuthToken()
  if (!token) {
    throw new Error('Not authenticated')
  }

  if (!BACKEND_URL) {
    throw new Error('Backend URL not configured')
  }

  const response = await fetch(`${BACKEND_URL}/api/gmail/oauth/url`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({ permission }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = 'Failed to get OAuth URL'
    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.detail || errorMessage
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage)
  }

  const data = await response.json()
  return data.url
}

// Open Gmail OAuth for Office Add-ins (Excel)
// Opens Google OAuth URL directly in an Office dialog
async function openGmailAuthDialogOffice(
  permission: 'read' | 'read_send' = 'read'
): Promise<{
  success: boolean
  email?: string
} | null> {
  // First, get the OAuth URL from the backend
  const googleOAuthUrl = await getGoogleOAuthUrl(permission)

  return new Promise((resolve, reject) => {
    let resolved = false
    let dialog: Office.DialogHandler

    const handler = (event: Office.DialogParentMessageReceivedEventArgs) => {
      if (!resolved) {
        resolved = true
        try {
          const message = JSON.parse(event.message) as DialogResponse

          if (message.error) {
            console.error('Gmail auth error from dialog:', message.error)
            dialog.close()
            setTimeout(() => reject(new Error(message.error)), 100)
          } else if (message.success && message.email) {
            dialog.close()
            const result = {
              success: true,
              email: message.email,
            }
            setTimeout(() => resolve(result), 100)
          } else {
            dialog.close()
            setTimeout(
              () => reject(new Error('Invalid response from Gmail auth')),
              100
            )
          }
        } catch (error) {
          console.error('Failed to parse Gmail auth response:', error)
          dialog.close()
          setTimeout(
            () => reject(new Error('Failed to parse Gmail auth response')),
            100
          )
        }
      }
    }

    console.log('Opening Gmail auth dialog directly to Google:', googleOAuthUrl)

    // Open dialog directly to Google OAuth URL
    Office.context.ui.displayDialogAsync(
      googleOAuthUrl,
      { height: 70, width: 40 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          dialog = result.value
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            handler
          )

          dialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            (event) => {
              const errorOrMessage = event.error

              if (errorOrMessage === 12002) {
                if (!resolved) {
                  resolved = true
                  reject(new Error('Gmail auth dialog closed by user'))
                }
              } else if (errorOrMessage === 12006) {
                if (!resolved) {
                  resolved = true
                  reject(new Error('Gmail auth dialog navigation failed'))
                }
              }
            }
          )
        } else {
          reject(
            new Error(
              `Failed to open Gmail auth dialog: ${result.error?.message || result.status}`
            )
          )
        }
      }
    )
  })
}

// Open Gmail OAuth for Google Sheets (popup window)
// Opens Google OAuth URL directly in a popup
async function openGmailAuthDialogPopup(
  permission: 'read' | 'read_send' = 'read'
): Promise<{
  success: boolean
  email?: string
} | null> {
  // First, get the OAuth URL from the backend
  const googleOAuthUrl = await getGoogleOAuthUrl(permission)

  return new Promise((resolve, reject) => {
    // Clear any previous result
    localStorage.removeItem('gmail_oauth_result')
    localStorage.removeItem('gmail_oauth_timestamp')

    console.log('Opening Gmail auth popup directly to Google:', googleOAuthUrl)

    // Open popup window directly to Google OAuth
    const width = 500
    const height = 600
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2
    const popup = window.open(
      googleOAuthUrl,
      'gmail_oauth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    )

    if (!popup) {
      reject(new Error('Failed to open popup window. Please allow popups.'))
      return
    }

    let resolved = false

    // Listen for postMessage from callback page
    const messageHandler = (event: MessageEvent) => {
      if (event.data?.type === 'gmail_oauth_result' && !resolved) {
        resolved = true
        window.removeEventListener('message', messageHandler)
        clearInterval(pollInterval)

        if (event.data.error) {
          reject(new Error(event.data.error))
        } else if (event.data.success && event.data.email) {
          resolve({ success: true, email: event.data.email })
        } else {
          reject(new Error('Invalid OAuth response'))
        }

        try {
          popup.close()
        } catch {
          // Ignore close errors
        }
      }
    }
    window.addEventListener('message', messageHandler)

    // Also poll localStorage as backup
    const pollInterval = setInterval(() => {
      if (resolved) {
        clearInterval(pollInterval)
        return
      }

      // Check if popup was closed
      if (popup.closed) {
        clearInterval(pollInterval)
        window.removeEventListener('message', messageHandler)

        // Check localStorage one more time
        const result = localStorage.getItem('gmail_oauth_result')
        if (result && !resolved) {
          resolved = true
          try {
            const data = JSON.parse(result) as DialogResponse
            localStorage.removeItem('gmail_oauth_result')
            localStorage.removeItem('gmail_oauth_timestamp')

            if (data.error) {
              reject(new Error(data.error))
            } else if (data.success && data.email) {
              resolve({ success: true, email: data.email })
            } else {
              reject(new Error('Invalid OAuth response'))
            }
          } catch {
            reject(new Error('Failed to parse OAuth response'))
          }
        } else if (!resolved) {
          resolved = true
          reject(new Error('Gmail auth cancelled'))
        }
        return
      }

      // Check localStorage
      const result = localStorage.getItem('gmail_oauth_result')
      const timestamp = localStorage.getItem('gmail_oauth_timestamp')
      if (result && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10)
        if (age < 30000) {
          // Result is fresh (< 30 seconds old)
          resolved = true
          clearInterval(pollInterval)
          window.removeEventListener('message', messageHandler)

          try {
            const data = JSON.parse(result) as DialogResponse
            localStorage.removeItem('gmail_oauth_result')
            localStorage.removeItem('gmail_oauth_timestamp')

            if (data.error) {
              reject(new Error(data.error))
            } else if (data.success && data.email) {
              resolve({ success: true, email: data.email })
            } else {
              reject(new Error('Invalid OAuth response'))
            }

            try {
              popup.close()
            } catch {
              // Ignore close errors
            }
          } catch {
            reject(new Error('Failed to parse OAuth response'))
          }
        }
      }
    }, 500)

    // Timeout after 5 minutes
    setTimeout(() => {
      if (!resolved) {
        resolved = true
        clearInterval(pollInterval)
        window.removeEventListener('message', messageHandler)
        try {
          popup.close()
        } catch {
          // Ignore close errors
        }
        reject(new Error('Gmail auth timeout'))
      }
    }, 5 * 60 * 1000)
  })
}

export async function openGmailAuthDialog(
  permission: 'read' | 'read_send' = 'read'
): Promise<{
  success: boolean
  email?: string
} | null> {
  if (!BACKEND_URL) {
    throw new Error('Backend URL not configured')
  }

  if (isOfficeEnvironment()) {
    console.log('Using Office Dialog API for Gmail auth')
    return openGmailAuthDialogOffice(permission)
  } else {
    console.log('Using popup window for Gmail auth')
    return openGmailAuthDialogPopup(permission)
  }
}
